import { Add, DoneAll, Download, Print } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import { applySolidInversion } from "@mui/joy/colorInversion";
import { useMutation } from "@tanstack/react-query";
import { Outlet } from "@tanstack/react-router";
import { ApiCompetition, ApiCompetitionEntryInline } from "griptonite-api-client";
import { useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import api from "@/core/api";
import {
  useBulkUpdateCompetitionAttemptsMutation,
  useCompetitionEntriesQuery,
  useCompetitionQuery,
  useCurrentCompetitionUuid,
  useGenerateCompetitionLeaderboardPdfMutation,
} from "@/hooks/competitions";
import downloadBlob from "@/utils/downloadBlob";

import { ButtonLink } from "../Links";
import { useModal } from "../modals/useModal";
import { EntriesEditContextProvider, useEntriesEditContext } from "./CompetitionEntriesEditContext";
import CompetitionEntriesTable from "./CompetitionEntriesTable";

const EntriesFooter = ({
  entries,
  filteredEntries,
}: {
  entries: ApiCompetitionEntryInline[];
  filteredEntries: ApiCompetitionEntryInline[];
}) => {
  const { attemptState, clearAttempts } = useEntriesEditContext();
  const competitionUuid = useCurrentCompetitionUuid();
  const { data: competition } = useCompetitionQuery(competitionUuid);

  const changesCount = Object.keys(attemptState).length;

  const mutation = useBulkUpdateCompetitionAttemptsMutation();

  const { display: displayModal } = useModal();

  const saveChanges = () => {
    if (!competitionUuid) {
      return;
    }
    mutation.mutate(
      {
        competitionUuid,
        attempts: Object.entries(attemptState).map(
          ([key, { success, tries, segmentsCount, segmentTries }]) => {
            const [userUuid, routeUuid] = key.split(":");
            return {
              user: userUuid,
              route: routeUuid,
              count: tries,
              success,
              segmentsCount,
              segmentTriesCount: segmentTries,
            };
          },
        ),
      },
      {
        onSuccess: () => clearAttempts(),
        onError: () => {
          alert("Failed to save changes. Please try again or contact support");
        },
      },
    );
  };

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        padding: 0.5,
        paddingX: 2,
        ...(changesCount
          ? {
              backgroundImage: "var(--joy-palette-primary-solidGradientBg)",
              color: "white",
            }
          : {}),
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={changesCount ? [applySolidInversion("primary")] : []}
      >
        <Typography level="body-md" textColor="inherit">
          <strong>{filteredEntries.length}</strong> of {entries.length} total competitors.
        </Typography>
        <ButtonLink
          startDecorator={<Add />}
          from="/gym/$venueUuid/events/$competitionUuid/competitors"
          to="./create"
          replace
          sx={changesCount ? { backgroundImage: "none" } : {}}
          size="sm"
        >
          Add new
        </ButtonLink>
      </Stack>
      {changesCount ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={changesCount ? [applySolidInversion("primary")] : []}
        >
          <Typography fontWeight="bold" textColor="inherit">
            {changesCount} change{changesCount > 1 ? "s" : ""} made.
          </Typography>
          <Button
            onClick={saveChanges}
            loading={mutation.isPending}
            loadingPosition="start"
            sx={changesCount ? { backgroundImage: "none" } : {}}
          >
            Save
          </Button>
          <Button variant="outlined" onClick={() => clearAttempts()}>
            Clear
          </Button>
        </Stack>
      ) : competition?.status === "PENDING_RESULTS" ? (
        <Button
          startDecorator={<DoneAll />}
          onClick={() =>
            competitionUuid &&
            displayModal("CONFIRM_PUBLISH_COMPETITION_RESULTS", {
              competitionUuid,
            })
          }
        >
          Confirm Results & Publish
        </Button>
      ) : null}
    </Stack>
  );
};

const CategoriesSelect = ({ competition }: { competition: ApiCompetition }) => {
  const { selectedCategoryUuid, setSelectedCategoryUuid } = useEntriesEditContext();

  return (
    <Select
      value={selectedCategoryUuid ?? ""}
      onChange={(_e, value) => {
        setSelectedCategoryUuid(value || null);
      }}
      renderValue={(value) => {
        if (value?.value === "") {
          return "All Categories";
        }
        return `Category: ${value?.label}`;
      }}
    >
      <Option key="all" value={""}>
        All
      </Option>
      {competition?.stages[0].categories.map((category) => (
        <Option key={category.uuid} value={category.uuid}>
          {category.name}
        </Option>
      ))}
    </Select>
  );
};

const FiltersBar = ({ competitionUuid }: { competitionUuid: string }) => {
  const { data: competition } = useCompetitionQuery(competitionUuid);

  const generatePdfMutation = useGenerateCompetitionLeaderboardPdfMutation();

  const generatePdf = () => {
    generatePdfMutation.mutateAsync(competitionUuid, {
      onError: () => alert("Failed to generate PDF. Please try again"),
      onSuccess: (url) => {
        window.open(url, "_blank");
      },
    });
  };

  const downloadXlsx = async () => {
    const response = await api.competitions.getCompetitionLeaderboardXlsx(competitionUuid);
    if (!response.ok) {
      throw new Error("Failed to download Excel file");
    }
    downloadBlob(
      await response.blob(),
      `${competition ? competition.name + " " : ""}leaderboard.xlsx`,
    );
  };

  const downloadXlsxBlobMutation = useMutation({
    mutationFn: downloadXlsx,
  });

  return (
    <Stack
      direction="row"
      sx={{
        padding: 1,
        gap: 1,
        borderBottom: "1px solid",
        borderColor: "neutral.200",
      }}
    >
      {competition ? <CategoriesSelect competition={competition} /> : null}
      <CompetitorSearch competitionUuid={competitionUuid} />
      <Box sx={{ flex: 1 }}></Box>
      <Tooltip title="Generate printable leaderboard">
        <IconButton
          onClick={generatePdf}
          loading={generatePdfMutation.isPending}
          disabled={generatePdfMutation.isPending}
        >
          <Print />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download leaderboard as Excel spreadsheet">
        <IconButton
          onClick={() => downloadXlsxBlobMutation.mutate()}
          disabled={downloadXlsxBlobMutation.isPending}
          loading={downloadXlsxBlobMutation.isPending}
        >
          <Download />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

const CompetitorSearch = (_props: { competitionUuid: string }) => {
  const { competitorSearchQuery, setCompetitorSearchQuery } = useEntriesEditContext();

  const [query, setQuery] = useState(competitorSearchQuery);
  const debouncedUpdate = useDebouncedCallback((q) => {
    setCompetitorSearchQuery(q);
  }, 150);

  return (
    <Input
      placeholder="Search competitors..."
      value={query}
      onChange={(e) => {
        setQuery(e.target.value);
        if (e.target.value === "") {
          setCompetitorSearchQuery("");
        } else {
          debouncedUpdate(e.target.value);
        }
      }}
    />
  );
};

const emptyArray: never[] = [];

function CompetitionEntries({ competitionUuid }: { competitionUuid: string }) {
  const entriesQuery = useCompetitionEntriesQuery(competitionUuid);

  const { selectedCategoryUuid, competitorSearchQuery } = useEntriesEditContext();

  const entries = entriesQuery.data || emptyArray;
  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      const categoryMatches = !selectedCategoryUuid || entry.category === selectedCategoryUuid;
      if (!categoryMatches) {
        return false;
      }
      const searchMatches =
        !competitorSearchQuery ||
        entry.user.name.toLowerCase().includes(competitorSearchQuery.toLowerCase());
      return searchMatches;
    });
  }, [entries, selectedCategoryUuid, competitorSearchQuery]);

  return (
    <>
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <FiltersBar competitionUuid={competitionUuid} />
        <CompetitionEntriesTable competitionUuid={competitionUuid} entries={filteredEntries} />
        <EntriesFooter entries={entries} filteredEntries={filteredEntries} />
      </Stack>
      <Outlet />
    </>
  );
}

export default function CompetitionEntriesWrapped({
  competitionUuid,
}: {
  competitionUuid: string;
}) {
  return (
    <EntriesEditContextProvider key={competitionUuid}>
      <CompetitionEntries competitionUuid={competitionUuid} />
    </EntriesEditContextProvider>
  );
}
